// The one API client of the app and the query keys every route shares.
// Types come from @saf/api-client (generated); nothing here is hand-typed.

import { createApi, errorDetail, type components } from "@saf/api-client";
import { queryOptions } from "@tanstack/react-query";

export type AirframeSummary = components["schemas"]["AirframeSummary"];
export type AirframeDetail = components["schemas"]["AirframeDetail"];
export type AirframeList = components["schemas"]["AirframeList"];
export type Me = components["schemas"]["Me"];
export type ClientConfig = components["schemas"]["ClientConfig"];
export type CrewMember = components["schemas"]["CrewMember"];
export type MemberEntry = components["schemas"]["MemberEntry"];
export type InviteSummary = components["schemas"]["InviteSummary"];
export type Transfer = components["schemas"]["Transfer"];
export type Feed = components["schemas"]["Feed"];
export type FlightList = components["schemas"]["FlightList"];
export type LogbookList = components["schemas"]["LogbookList"];
export type LiveCondition = components["schemas"]["LiveCondition"];
export type LivePosition = components["schemas"]["LivePosition"];
export type TrailPoint = components["schemas"]["TrailPoint"];
export type LiveFlight = components["schemas"]["LiveFlight"];
export type CockpitAccess = components["schemas"]["CockpitAccess"];
export type LogbookFlight = components["schemas"]["LogbookFlight"];
export type MaintenancePage = components["schemas"]["MaintenancePage"];
export type TechLogList = components["schemas"]["TechLogList"];
export type TechLogEntry = components["schemas"]["TechLogEntry"];
export type Flight = components["schemas"]["Flight"];
export type FlightDetail = components["schemas"]["FlightDetail"];
export type Track = components["schemas"]["Track"];
export type Analysis = components["schemas"]["Analysis"];
export type Exceedance = components["schemas"]["Exceedance"];
export type Landing = components["schemas"]["Landing"];
export type EventList = components["schemas"]["EventList"];
export type LedgerSummary = components["schemas"]["LedgerSummary"];
export type LeaseSummary = components["schemas"]["LeaseSummary"];
export type Preferences = components["schemas"]["Preferences"];
export type Role = components["schemas"]["Role"];
export type Visibility = components["schemas"]["Visibility"];
/** What a channel's pointer says (decision 137 amended): the version and the systems built. */
export type ChannelRelease = components["schemas"]["ChannelRelease"];
export type JoinPolicy = components["schemas"]["JoinPolicy"];
export type Schedule = components["schemas"]["Schedule"];
export type ScheduleAirframe = components["schemas"]["ScheduleAirframe"];
export type Slot = components["schemas"]["Slot"];
export type ConflictList = components["schemas"]["ConflictList"];

/** A failed request, with the envelope's code and message when the API sent one. */
export class ApiError extends Error {
  readonly status: number;
  readonly code: string;
  readonly details?: unknown;

  constructor(status: number, body: unknown) {
    const detail = errorDetail(body);
    super(detail?.message ?? `request failed with status ${status}`);
    this.name = "ApiError";
    this.status = status;
    this.code = detail?.code ?? "request_failed";
    this.details = detail?.details;
  }
}

/** What the client is set to: the platform's address and the token under the host (decision 064), nothing in the browser. */
export type ApiSetup = { baseUrl: string; bearer: string | null; client: string | null };

/**
 * The one client. In the browser it calls this site's /api with the session
 * cookie; under the companion's shell `configureApi` points it at the
 * platform the shell is set to, with the token the shell holds and the
 * companion's name in the client header, so the platform judges the versions
 * (F408) as it does for the Rust side.
 */
export let api = withAdminView(createApi());

/** The client header the shell gave (`companion/0.2.0-88`), null in a browser: the error reports say which build showed the page (decision 140). */
let client: string | null = null;
export function clientHeader(): string | null {
  return client;
}

export function configureApi(setup: ApiSetup): void {
  client = setup.client ?? null;
  api = withAdminView(
    createApi({
      baseUrl: setup.baseUrl,
      bearer: setup.bearer ?? undefined,
      headers: setup.client ? { "x-saf-client": setup.client } : undefined,
    }),
  );
}

/**
 * The administrator's view (decision 131): while it is on, every request
 * carries `X-SAF-View: admin`, and the platform lets an administrator read
 * an airframe's record as a member does (and nobody else anything more).
 * The airframe's route turns it on while its address says `?admin`, off
 * when the page is left: outside that page an administrator is the pilot
 * they are.
 */
export const ADMIN_VIEW_HEADER = "x-saf-view";
let adminView = false;

/** Turns the view on or off; true when that changed what the requests say. */
export function setAdminView(on: boolean): boolean {
  const changed = adminView !== on;
  adminView = on;
  return changed;
}

export function isAdminView(): boolean {
  return adminView;
}

function withAdminView<T extends ReturnType<typeof createApi>>(client: T): T {
  client.use({
    onRequest({ request }) {
      if (adminView) request.headers.set(ADMIN_VIEW_HEADER, "admin");
      return undefined;
    },
  });
  return client;
}

/** Throws `ApiError` on a failed response, so queries and mutations only see data. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || !result.response.ok) {
    throw new ApiError(result.response.status, result.error);
  }
  return result.data as T;
}

export const queries = {
  /** The release a channel points at: its version and the systems it has an installer for. */
  release: (channel: "latest" | "stable" | "beta") =>
    queryOptions({
      queryKey: ["release", channel],
      queryFn: async () => unwrap<ChannelRelease>(await api.GET("/api/v1/releases/{channel}", { params: { path: { channel } } })),
      staleTime: 300_000,
    }),
  me: () =>
    queryOptions({
      queryKey: ["me"],
      queryFn: async () => {
        const r = await api.GET("/api/v1/me");
        if (r.response.status === 401) return null;
        return unwrap<Me>(r);
      },
      staleTime: 60_000,
    }),
  airframes: (scope: "public" | "member" | "mine", q: string, more: { joined?: "exclude" | "include"; continent?: string; sort?: string } = {}) =>
    queryOptions({
      queryKey: ["airframes", scope, q, more.joined ?? "", more.continent ?? "", more.sort ?? ""],
      queryFn: async () =>
        unwrap<AirframeList>(
          await api.GET("/api/v1/airframes", {
            params: { query: { scope, q: q || undefined, limit: 100, joined: more.joined, continent: more.continent || undefined, sort: more.sort } },
          }),
        ),
    }),
  airframe: (id: string) =>
    queryOptions({
      queryKey: ["airframe", id],
      queryFn: async () => unwrap<AirframeDetail>(await api.GET("/api/v1/airframes/{id}", { params: { path: { id } } })),
    }),
  members: (id: string, status: string) =>
    queryOptions({
      queryKey: ["members", id, status],
      queryFn: async () =>
        unwrap<MemberEntry[]>(
          await api.GET("/api/v1/airframes/{id}/members", { params: { path: { id }, query: { status } } }),
        ),
    }),
  invites: (id: string) =>
    queryOptions({
      queryKey: ["invites", id],
      queryFn: async () =>
        unwrap<InviteSummary[]>(await api.GET("/api/v1/airframes/{id}/invites", { params: { path: { id } } })),
    }),
  techLog: (id: string, flightId?: string) =>
    queryOptions({
      queryKey: ["tech-log", id, flightId ?? ""],
      queryFn: async () =>
        unwrap<TechLogList>(
          await api.GET("/api/v1/airframes/{id}/tech-log", { params: { path: { id }, query: { limit: 200, flight_id: flightId } } }),
        ),
    }),
  /** The returns waiting on this airframe (the administrator's view counts them, decision 131). */
  conflicts: (id: string) =>
    queryOptions({
      queryKey: ["conflicts", id],
      queryFn: async () => unwrap<ConflictList>(await api.GET("/api/v1/airframes/{id}/conflicts", { params: { path: { id } } })),
    }),
  maintenance: (id: string) =>
    queryOptions({
      queryKey: ["maintenance", id],
      queryFn: async () =>
        unwrap<MaintenancePage>(await api.GET("/api/v1/airframes/{id}/maintenance", { params: { path: { id } } })),
    }),
  flights: (id: string) =>
    queryOptions({
      queryKey: ["flights", id],
      queryFn: async () =>
        unwrap<FlightList>(
          await api.GET("/api/v1/airframes/{id}/flights", { params: { path: { id }, query: { limit: 100 } } }),
        ),
    }),
  /** The shared cockpit's key under the live booking (F464): null when none is shared. */
  cockpit: (id: string) =>
    queryOptions({
      queryKey: ["cockpit", id],
      queryFn: async () => unwrap<CockpitAccess | null>(await api.GET("/api/v1/airframes/{id}/cockpit", { params: { path: { id } } })),
    }),
  /** The live condition of an airframe (F458): where it is and the trail of the session, null when nobody flies it. */
  live: (id: string) =>
    queryOptions({
      queryKey: ["live", id],
      queryFn: async () => unwrap<LiveCondition | null>(await api.GET("/api/v1/airframes/{id}/live", { params: { path: { id } } })),
    }),
  /** The platform's settings a client draws with (F468): the Google Maps key and map id, null without; a visitor is told nothing (401 reads as no maps). */
  config: () =>
    queryOptions({
      queryKey: ["config"],
      queryFn: async () => {
        const r = await api.GET("/api/v1/config");
        if (r.response.status === 401) return { maps: null } as ClientConfig;
        return unwrap<ClientConfig>(r);
      },
      staleTime: Infinity,
    }),
  /** The logbook across the airframes (F457): the caller's own flights, or the fleet's; a page at a time. */
  logbook: (scope: "mine" | "fleet", filters: { airframe?: string; pilot?: string; q?: string }, offset: number, limit: number) =>
    queryOptions({
      queryKey: ["logbook", scope, filters.airframe ?? "", filters.pilot ?? "", filters.q ?? "", offset, limit],
      queryFn: async () =>
        unwrap<LogbookList>(
          await api.GET("/api/v1/flights", {
            params: {
              query: {
                scope,
                airframe_id: filters.airframe || undefined,
                pilot: filters.pilot || undefined,
                q: filters.q || undefined,
                limit,
                offset,
              },
            },
          }),
        ),
    }),
  flight: (id: string, fid: string) =>
    queryOptions({
      queryKey: ["flight", id, fid],
      queryFn: async () =>
        unwrap<FlightDetail>(await api.GET("/api/v1/airframes/{id}/flights/{fid}", { params: { path: { id, fid } } })),
      staleTime: 300_000,
    }),
  events: (id: string) =>
    queryOptions({
      queryKey: ["events", id],
      queryFn: async () =>
        unwrap<EventList>(
          await api.GET("/api/v1/airframes/{id}/events", { params: { path: { id }, query: { limit: 100 } } }),
        ),
    }),
  ledger: (id: string) =>
    queryOptions({
      queryKey: ["ledger", id],
      queryFn: async () => unwrap<LedgerSummary>(await api.GET("/api/v1/airframes/{id}/ledger", { params: { path: { id } } })),
    }),
  notifications: () =>
    queryOptions({
      queryKey: ["notifications"],
      queryFn: async () => unwrap<Feed>(await api.GET("/api/v1/me/notifications", { params: { query: { limit: 100 } } })),
      refetchInterval: 10_000,
    }),
  preferences: () =>
    queryOptions({
      queryKey: ["preferences"],
      queryFn: async () => unwrap<Preferences>(await api.GET("/api/v1/me/preferences")),
    }),
  transfers: () =>
    queryOptions({
      queryKey: ["transfers"],
      queryFn: async () => unwrap<Transfer[]>(await api.GET("/api/v1/transfers")),
    }),
  versions: (id: string) =>
    queryOptions({
      queryKey: ["versions", id],
      queryFn: async () =>
        unwrap<components["schemas"]["VersionList"]>(
          await api.GET("/api/v1/airframes/{id}/versions", { params: { path: { id }, query: { limit: 100 } } }),
        ),
    }),
  /** The calendar (F520, decision 128): the aircraft the caller is a member of, their bookings and slots in the window. */
  schedule: (from: string, to: string) =>
    queryOptions({
      queryKey: ["schedule", from, to],
      queryFn: async () => unwrap<Schedule>(await api.GET("/api/v1/schedule", { params: { query: { from, to } } })),
      refetchInterval: 20_000,
    }),
  airframeSlots: (id: string, from: string, to: string) =>
    queryOptions({
      queryKey: ["slots", id, from, to],
      queryFn: async () =>
        unwrap<components["schemas"]["SlotList"]>(await api.GET("/api/v1/airframes/{id}/slots", { params: { path: { id }, query: { from, to } } })),
      refetchInterval: 20_000,
    }),
  adminOverview: () =>
    queryOptions({
      queryKey: ["admin", "overview"],
      queryFn: async () => unwrap<components["schemas"]["Overview"]>(await api.GET("/api/v1/admin/overview")),
      refetchInterval: 30_000,
    }),
  adminUsers: (q: string) =>
    queryOptions({
      queryKey: ["admin", "users", q],
      queryFn: async () =>
        unwrap<components["schemas"]["AdminUserList"]>(
          await api.GET("/api/v1/admin/users", { params: { query: { q: q || undefined, limit: 100 } } }),
        ),
    }),
  adminAirframes: (q: string) =>
    queryOptions({
      queryKey: ["admin", "airframes", q],
      queryFn: async () =>
        unwrap<components["schemas"]["AdminAirframeList"]>(
          await api.GET("/api/v1/admin/airframes", { params: { query: { q: q || undefined, limit: 100 } } }),
        ),
    }),
  adminLeases: (scope: "active" | "all") =>
    queryOptions({
      queryKey: ["admin", "leases", scope],
      queryFn: async () =>
        unwrap<components["schemas"]["AdminLeaseList"]>(
          await api.GET("/api/v1/admin/leases", { params: { query: { scope, limit: 100 } } }),
        ),
    }),
  /** The check-ins the platform refused for what they said (F409), for the panel (F411). */
  adminRefusals: (q: string) =>
    queryOptions({
      queryKey: ["admin", "refusals", q],
      queryFn: async () =>
        unwrap<components["schemas"]["RefusalList"]>(
          await api.GET("/api/v1/admin/refusals", { params: { query: { q: q || undefined, limit: 100 } } }),
        ),
    }),
  /** The errors the pilots' apps and pages reported (decision 140, F524). */
  adminClientErrors: (q: string) =>
    queryOptions({
      queryKey: ["admin", "client-errors", q],
      queryFn: async () =>
        unwrap<components["schemas"]["ClientErrorList"]>(
          await api.GET("/api/v1/admin/client-errors", { params: { query: { q: q || undefined, limit: 100 } } }),
        ),
    }),
  adminErrors: (q: string) =>
    queryOptions({
      queryKey: ["admin", "errors", q],
      queryFn: async () =>
        unwrap<components["schemas"]["ErrorLog"]>(
          await api.GET("/api/v1/admin/errors", { params: { query: { q: q || undefined, limit: 100 } } }),
        ),
    }),
};

/** Where the Discord login starts; `next` is a path on this site. */
export function loginUrl(next: string): string {
  return `/api/v1/auth/discord/start?next=${encodeURIComponent(next)}`;
}
